import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Disc3, Pause, Play, ShoppingBag } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import Breadcrumbs from "@/components/Breadcrumbs";
import { ChargedInAed } from "@/components/CurrencySelector";
import { useTrackActions } from "@/components/CatalogPrimitives";
import { useCurrency } from "@/lib/CurrencyContext";
import { useCart } from "@/lib/CartContext";
import { useAuth } from "@/lib/AuthContext";
import { useLocale, restoreBaselineHead } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import { api, ASSET_BASE } from "@/lib/api";
import { fetchPacks } from "@/lib/usePacks";

const albumName = (p, lang) => (lang === "ar" && p.name_ar ? p.name_ar : p.name_en);
const albumCat = (p, lang) => (lang === "ar" && p.category_ar ? p.category_ar : p.category || "");
const albumDesc = (p, lang) => (lang === "ar" && p.description_ar ? p.description_ar : p.description_en);
const img = (u) => `${ASSET_BASE}${u}`;
export const albumPath = (slug) => `/music/albums/${slug}`;

function AlbumArt({ album, className = "", small = false }) {
  const { lang } = useLocale();
  if (small && !album.artwork_url) {
    return <div className={`bg-[#111] border border-red-brand/30 flex items-center justify-center ${className}`} data-testid="album-artwork-placeholder"><Disc3 className="text-red-brand/70" size={34} /></div>;
  }
  if (album.artwork_url) {
    const src = small ? `${img(album.artwork_url)}&size=thumb` : img(album.artwork_url);
    return <img src={src} alt={album.name_en} loading={small ? "lazy" : "eager"} className={`object-cover ${className}`} data-testid="album-artwork" />;
  }
  return (
    <div className={`relative bg-[#0b0b0b] border border-white/10 flex flex-col justify-between p-6 ${className}`} data-testid="album-artwork-placeholder">
      <Disc3 className="text-red-brand/70 self-end" size={28} />
      <div>
        <div className="h-px w-10 bg-red-brand mb-4" />
        <p className="font-anton text-2xl md:text-3xl leading-tight text-white/90">{albumName(album, lang)}</p>
      </div>
    </div>
  );
}

function OffBadge({ pct }) {
  const { t } = useTranslation();
  return <span className="absolute top-3 start-3 z-10 bg-red-brand text-white font-mono text-[11px] tracking-widest px-2.5 py-1 rounded" data-testid="album-off-badge">{t("packs.off", { pct: Math.round(pct) })}</span>;
}

export function useAlbums() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  useEffect(() => { fetchPacks(true).then(setItems); }, [user?.id]);
  return items;
}

// Fourth collection card on /music — same shell as Premium / Free / Edits & Funky.
export function AlbumCollectionCard({ album }) {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const to = localisedTo(albumPath(album.slug), lang);
  return (
    <article data-testid={`album-card-${album.slug}`}
             className="group relative collection-card collection-card--premium px-7 py-8 sm:px-10 sm:py-12 flex flex-col">
      <span className="collection-card__shine" aria-hidden="true" />
      <span className="absolute top-5 end-5 z-10 bg-red-brand text-white font-mono text-[11px] tracking-widest px-2.5 py-1 rounded" data-testid="album-off-badge">
        {t("packs.off", { pct: Math.round(album.discount_percent) })}
      </span>
      <div className="collection-card__body flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-4 pe-24">
          <Disc3 size={18} className="text-red-brand" />
          <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-red-brand">
            {albumCat(album, lang) || t("packs.albumLabel")} · {t("packs.tracks", { count: album.track_count })}
          </div>
        </div>
        <div className="flex items-start gap-5 mb-6">
          <Link to={to} className="shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-xl overflow-hidden block" data-testid={`album-card-art-${album.slug}`}>
            <AlbumArt album={album} small className="w-full h-full" />
          </Link>
          <Link to={to} className="font-anton text-3xl sm:text-4xl lg:text-5xl leading-[0.95] uppercase text-red-brand hover:text-white transition-colors" data-testid={`album-card-title-${album.slug}`}>
            {albumName(album, lang)}
          </Link>
        </div>
        <div className="mt-auto space-y-5">
          <PriceBlock album={album} compact />
          <AddAlbumButton album={album} />
        </div>
      </div>
    </article>
  );
}

function Row({ label, value, testid, ltr = true }) {
  return (
    <div className="flex items-center justify-between font-mono text-xs tracking-[0.15em] text-white/70" data-testid={testid}>
      <span>{label}</span><span dir={ltr ? "ltr" : undefined}>{value}</span>
    </div>
  );
}

function PriceBlock({ album, compact = false }) {
  const { t } = useTranslation();
  const { format: fmt } = useCurrency();
  const partial = album.owned_count > 0 && !album.all_owned;
  const price = partial ? album.your_price : album.final_price;
  return (
    <div className="space-y-2" data-testid="album-price-block">
      <Row label={t("packs.originalValue")} value={<s className="text-white/40">{fmt(partial ? album.your_original : album.original_total)}</s>} testid="album-original-value" />
      <Row label={t("packs.discount")} value={<span className="text-red-brand">{t("packs.off", { pct: Math.round(album.discount_percent) })}</span>} testid="album-discount" ltr={false} />
      <Row label={t("packs.savings")} value={<span className="text-red-brand">−{fmt(partial ? album.your_savings : album.savings)}</span>} testid="album-savings" />
      <div className="flex items-end justify-between pt-3 border-t border-white/10">
        <span className="font-mono text-[10px] tracking-[0.3em] text-white/50">{partial ? t("packs.yourPrice") : t("packs.finalPrice")}</span>
        <span className={`font-anton whitespace-nowrap ${compact ? "text-3xl" : "text-4xl"}`} dir="ltr" data-testid="album-final-price">{fmt(price)}</span>
      </div>
      {partial && <p className="text-xs text-white/60" data-testid="album-owned-note">{t("packs.ownedNote", { owned: album.owned_count, total: album.track_count })}</p>}
      <ChargedInAed aedTotal={price} testid="album-charged-in-aed" className="rounded-lg border border-red-brand/40 bg-red-brand/[0.06] px-3 py-2" />
    </div>
  );
}

function AddAlbumButton({ album }) {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { addPack, hasPack, open } = useCart();
  const inCart = hasPack(album.id);
  if (album.all_owned) {
    return <div className="btn-ghost w-full justify-center opacity-70 cursor-not-allowed" data-testid="album-all-owned"><Check size={16} /> {t("packs.allOwned")}</div>;
  }
  const onAdd = () => {
    if (!inCart) {
      addPack(album);
      toast.success(t("packs.added", { name: albumName(album, lang) }));
    }
    open();
  };
  return (
    <button onClick={onAdd} disabled={album.is_preview} data-testid="album-add-to-cart" className="btn-primary w-full justify-center disabled:opacity-50">
      <ShoppingBag size={16} /> {inCart ? t("packs.inCart") : t("packs.addToCart")}
    </button>
  );
}

function TrackRow({ tr, index, actions }) {
  const { t } = useTranslation();
  const { lang } = useLocale();
  const playing = actions.playingId === tr.id;
  const title = lang === "ar" && tr.title_ar ? tr.title_ar : tr.title;
  return (
    <li className="flex items-center gap-3 py-3 border-b border-white/5" data-testid={`album-track-${tr.id}`}>
      <span className="w-6 font-mono text-xs text-white/30" dir="ltr">{String(index + 1).padStart(2, "0")}</span>
      <button onClick={() => actions.onPlayToggle(tr)} disabled={!tr.preview_url} data-testid={`album-track-play-${tr.id}`}
              aria-label={playing ? t("packs.pause") : t("packs.play")}
              className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center transition-colors disabled:opacity-30 ${playing ? "border-red-brand bg-red-brand/15" : "border-white/15 hover:border-red-brand"}`}>
        {playing ? <Pause size={14} /> : <Play size={14} />}
      </button>
      <img src={img(tr.cover_url)} alt="" className="w-11 h-11 rounded object-cover shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="truncate font-display tracking-normal" dir="auto">{title}</div>
        <div className="font-mono text-[10px] text-white/40 tracking-widest" dir="ltr">
          {(tr.track_type || "").toUpperCase()}
          {tr.bpm ? ` · ${tr.bpm} BPM` : ""}
          {tr.musical_key ? ` · ${tr.musical_key}` : ""}
          {tr.camelot ? ` · ${tr.camelot}` : ""}
        </div>
      </div>
      {tr.owned && <span className="font-mono text-[9px] tracking-widest text-emerald-400 border border-emerald-400/40 rounded px-1.5 py-0.5" data-testid={`album-track-owned-${tr.id}`}>{t("packs.owned")}</span>}
    </li>
  );
}

const SITE_ORIGIN = "https://djspyofficial.com";
// Arabic album page is indexable only when real Arabic name + description exist.
const arAlbumPublished = (a) => !!(a && a.name_ar && a.description_ar);

/** Per-album SEO: self canonical, description, OG/Twitter, hreflang, MusicAlbum JSON-LD. */
function useAlbumSeo(album, lang, missing) {
  useEffect(() => {
    if (!album && !missing) return undefined;
    const touched = [];
    const added = [];
    const setMeta = (key, value, attr = "name") => {
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      const created = !el;
      if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
      touched.push({ el, created, old: el.getAttribute("content") });
      el.setAttribute("content", value || "");
    };
    const addLink = (attrs) => {
      const l = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => l.setAttribute(k, v));
      document.head.appendChild(l);
      added.push(l);
    };
    if (missing && !album) {
      setMeta("robots", "noindex,nofollow");
      document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
    } else {
      const isAr = lang === "ar";
      const arOk = arAlbumPublished(album);
      const enUrl = `${SITE_ORIGIN}${albumPath(album.slug)}`;
      const arUrl = `${SITE_ORIGIN}/ar${albumPath(album.slug)}`;
      const canonical = isAr && arOk ? arUrl : enUrl;
      const name = albumName(album, lang);
      const desc = String(albumDesc(album, lang) || `${name} - DJ SPY`).slice(0, 300);
      const title = `${name} | DJ SPY`;
      const cover = album.artwork_url ? `${SITE_ORIGIN}${album.artwork_url}` : `${SITE_ORIGIN}/logo.png`;
      document.title = title;
      setMeta("description", desc);
      setMeta("robots", isAr && !arOk ? "noindex,follow" : "index,follow,max-image-preview:large");
      setMeta("og:type", "music.album", "property");
      setMeta("og:title", title, "property");
      setMeta("og:description", desc, "property");
      setMeta("og:url", canonical, "property");
      setMeta("og:image", cover, "property");
      setMeta("og:locale", isAr ? "ar_AE" : "en_US", "property");
      setMeta("twitter:card", "summary_large_image");
      setMeta("twitter:title", title);
      setMeta("twitter:description", desc);
      setMeta("twitter:image", cover);
      document.querySelectorAll('link[rel="canonical"]').forEach((n) => n.remove());
      addLink({ rel: "canonical", href: canonical });
      document.querySelectorAll('link[rel="alternate"][hreflang]').forEach((n) => n.remove());
      addLink({ rel: "alternate", hreflang: "en", href: enUrl });
      if (arOk) addLink({ rel: "alternate", hreflang: "ar", href: arUrl });
      addLink({ rel: "alternate", hreflang: "x-default", href: enUrl });
      const ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = "music-album-jsonld";
      ld.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "MusicAlbum",
        name,
        url: canonical,
        description: desc,
        image: cover,
        inLanguage: isAr ? "ar" : "en",
        byArtist: { "@type": "MusicGroup", name: "DJ SPY" },
        numTracks: album.track_count,
        track: (album.tracks || []).map((tr, i) => ({
          "@type": "MusicRecording", position: i + 1, name: tr.title,
        })),
      });
      document.head.appendChild(ld);
      added.push(ld);
    }
    return () => {
      added.forEach((n) => n.remove());
      touched.reverse().forEach(({ el, created, old }) => {
        if (created) el.remove(); else if (old !== null) el.setAttribute("content", old);
      });
      restoreBaselineHead();
    };
  }, [album, lang, missing]);
}

export function AlbumDetail() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { lang } = useLocale();
  const { user } = useAuth();
  const actions = useTrackActions();
  const [album, setAlbum] = useState(null);
  const [missing, setMissing] = useState(false);
  useAlbumSeo(album, lang, missing);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  useEffect(() => {
    setMissing(false);
    api.get(`/packs/${slug}`).then((r) => setAlbum(r.data)).catch(() => setMissing(true));
  }, [slug, user?.id]);
  return (
    <>
      <Nav />
      <main className="min-h-screen bg-black text-white pt-32 pb-24 px-6 md:px-10" data-testid="album-detail-page">
        <div className="max-w-[1400px] mx-auto">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Music", path: "/music" }, ...(album ? [{ name: albumName(album, lang), path: albumPath(album.slug) }] : [])]} testid="album-crumbs" />
          {missing && <p className="mt-10 text-white/60" data-testid="album-not-found">{t("packs.notFound")}</p>}
          {album && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              {album.is_preview && <div className="mt-6 inline-block bg-yellow-500/15 border border-yellow-500/40 text-yellow-300 font-mono text-[10px] tracking-widest px-3 py-1.5 rounded" data-testid="album-preview-badge">{t("packs.previewBadge")} · {album.status.toUpperCase()}</div>}
              <div className="mt-8 grid gap-10 lg:grid-cols-[1fr_1fr] items-start">
                <div className="relative rounded-2xl overflow-hidden">
                  <OffBadge pct={album.discount_percent} />
                  <AlbumArt album={album} className="w-full aspect-square rounded-2xl" />
                </div>
                <div className="lg:sticky lg:top-28">
                  <p className="font-mono text-xs tracking-[0.3em] text-red-brand">// {(albumCat(album, lang) || t("packs.albumLabel")).toUpperCase()}</p>
                  <h1 className="font-anton text-4xl sm:text-5xl lg:text-6xl mt-3 leading-none" data-testid="album-title">{albumName(album, lang)}</h1>
                  <p className="text-white/50 font-mono text-xs mt-3">{t("packs.tracks", { count: album.track_count })}</p>
                  {albumDesc(album, lang) && <p className="text-white/70 mt-5 leading-relaxed" data-testid="album-description">{albumDesc(album, lang)}</p>}
                  <div className="mt-8 hairline rounded-2xl p-5 bg-white/[0.02]">
                    <PriceBlock album={album} />
                    <div className="mt-5"><AddAlbumButton album={album} /></div>
                  </div>
                </div>
              </div>
              <section className="mt-14">
                <h2 className="font-mono text-xs tracking-[0.3em] text-white/50">{t("packs.tracklist")}</h2>
                <ol className="mt-4" data-testid="album-tracklist">
                  {(album.tracks || []).map((tr, i) => <TrackRow key={tr.id} tr={tr} index={i} actions={actions} />)}
                </ol>
              </section>
            </motion.div>
          )}
        </div>
        <actions.AudioElement />
      </main>
      <Footer />
    </>
  );
}

export function LegacyPackRedirect() {
  const { slug } = useParams();
  const { lang } = useLocale();
  return <Navigate replace to={localisedTo(slug ? albumPath(slug) : "/music", lang)} />;
}
